import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ApplicationButton, ApplicationSurface } from '../../components/application';
import AVIcon from '../../components/icons/AllVoicedIcons';
import { communityExperienceApi, createCommunityExperienceOperationKey } from './communityExperienceApi';
import { isCommunityExperienceAvailable } from './communityExperienceAvailability';
import { resolveCommunityExperienceAvailability } from './communityExperienceLocalReviewAvailability';

export default function CommunityExperienceDashboardInvitation({
  apiClient = communityExperienceApi,
  availability = resolveCommunityExperienceAvailability(),
}) {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [invitation, setInvitation] = useState(null);
  const claimKey = useRef(createCommunityExperienceOperationKey());
  const responseKeys = useRef({});
  const available = isCommunityExperienceAvailable(availability, i18n.resolvedLanguage);

  useEffect(() => {
    let active = true;
    if (!available) return () => { active = false; };
    apiClient.claimDashboardInvitation('en', claimKey.current)
      .then(({ data }) => {
        if (active && data?.presented) setInvitation(data);
      })
      .catch(() => {});
    return () => { active = false; };
  }, [apiClient, available]);

  if (!invitation) return null;

  const respond = async (action) => {
    responseKeys.current[action] ||= createCommunityExperienceOperationKey();
    try {
      await apiClient.respondToDashboardInvitation({
        expectedRevision: invitation.revision,
        action,
      }, responseKeys.current[action]);
      setInvitation(null);
    } catch (_error) {
      // Keep the claimed invitation visible when the response outcome is unknown.
    }
  };

  return <ApplicationSurface
    ariaLabelledby="ce-dashboard-invitation-title"
    className="av-dashboard-ce-invitation"
    variant="action"
  >
    <div aria-hidden="true" className="av-dashboard-ce-invitation__icon">
      <AVIcon icon="comments" size={22} />
    </div>
    <div className="av-dashboard-ce-invitation__copy">
      <h2 id="ce-dashboard-invitation-title">{t('ce_dashboard_invitation_title')}</h2>
      <p>{t('ce_dashboard_invitation_body')}</p>
    </div>
    <div className="av-dashboard-ce-invitation__actions">
      <ApplicationButton size="compact"
        onClick={() => navigate('/app/profile?tab=community-experience')}>
        {t('ce_dashboard_invitation_share')}
      </ApplicationButton>
      <ApplicationButton size="compact" variant="secondary"
        onClick={() => respond('maybe_later')}>
        {t('ce_dashboard_invitation_not_now')}
      </ApplicationButton>
      <ApplicationButton size="compact" variant="quiet"
        onClick={() => respond('no_thanks')}>
        {t('ce_dashboard_invitation_no_thanks')}
      </ApplicationButton>
    </div>
  </ApplicationSurface>;
}
